import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOrdersByType,
  ORDER_LIST_REDUX_SCOPE
} from '@/redux/order/maternityCenterOrderSlice';
import { formatDayString, renderTextStr } from '@/utils/renderUtils';
import { startHmFromPlan } from '@/utils/unassignedPlanTimeInit';

function NewOrders({ storeId }) {
  const dispatch = useDispatch();
  const dispatchOrderList = useSelector((state) => state.order?.order?.dispatchOrderList);
  const { data, query } = dispatchOrderList || {};
  const scrollRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const listQuery = { storeId: selectedStoreId, checkInStatus: '1' };
    const pull = () => {
      dispatch(fetchOrdersByType({
        query: listQuery,
        orderType: 'maternity_center',
        maternityCenterListMode: 'dispatchUnassigned',
        orderListScope: ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE,
        silent: true
      })).unwrap().catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 30000);
    return () => clearInterval(timer);
  }, [dispatch, selectedStoreId]);

  const records = useMemo(() => {
    if (String(query?.storeId ?? '') !== selectedStoreId) return [];
    if (String(query?.checkInStatus ?? '') !== '1') return [];
    return Array.isArray(data?.records) ? data.records : [];
  }, [data, query, selectedStoreId]);

  useEffect(() => {
    posRef.current = 0;
    if (scrollRef.current) {
      scrollRef.current.style.transform = 'translateY(0px)';
    }
  }, [records]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.15;
    const animate = () => {
      if (!paused && !selectedId && halfHeight > 0) {
        posRef.current += speed;
        if (posRef.current >= halfHeight) {
          posRef.current -= halfHeight;
        }
        el.style.transform = `translateY(${-posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, records, selectedId]);

  const orderFields = (item) => {
    const start = startHmFromPlan(item.startTime) || '--';
    const end = startHmFromPlan(item.endTime) || '--';
    return {
      name: renderTextStr(item.memberName),
      service: renderTextStr(item.projectName),
      room: `房号 ${renderTextStr(item.roomName)}`,
      day: formatDayString(item.appointmentDate) || '--',
      time: `${start} ~ ${end}`
    };
  };

  const selected = records.find((item) => String(item.id) === String(selectedId)) || null;

  const renderItem = (item, idx) => {
    const fields = orderFields(item);
    return (
      <div
        className="no-item no-item-clickable"
        key={`${item.id || 'o'}-${idx}`}
        onClick={() => setSelectedId(item.id != null ? String(item.id) : '')}
      >
        <div className="no-item-top">
          <span className="no-customer">{fields.name}</span>
          <span className="no-service">{fields.service}</span>
          <span className="no-room">{fields.room}</span>
        </div>
        <div className="no-item-bottom">
          <span className="no-date">{`预约 ${fields.day}`}</span>
          <span className="no-time">{fields.time}</span>
        </div>
      </div>
    );
  };

  const selectedFields = selected ? orderFields(selected) : null;

  return (
    <div className="new-orders-card" style={{ '--accent': '#a78bfa' }}>
      <div className="no-header">
        <span className="no-title">产康订单</span>
      </div>
      <div
        className="no-scroll-wrapper"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="no-scroll-inner" ref={scrollRef}>
          {records.map(renderItem)}
          {records.length > 0 ? records.map((item, idx) => renderItem(item, `dup-${idx}`)) : null}
        </div>
      </div>
      {selected && selectedFields && createPortal(
        <div className="ol-modal-overlay" onClick={() => setSelectedId('')}>
          <div className="ol-modal ol-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="ol-side">
              <div className="ol-side-title">产康订单</div>
              <div className="ol-side-wrap">
                <div className="ol-side-scroll">
                  {records.map((item) => {
                    const fields = orderFields(item);
                    const active = String(item.id) === String(selectedId);
                    return (
                      <div
                        key={item.id}
                        className={`ol-side-item${active ? ' active' : ''}`}
                        onClick={() => setSelectedId(item.id != null ? String(item.id) : '')}
                      >
                        <span className="ol-side-name">{fields.name}</span>
                        <span className="ol-side-service">{fields.service}</span>
                        <span className="ol-side-room">{fields.room}</span>
                        <span className="ol-side-time">{fields.day}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="ol-main">
              <div className="ol-modal-header">
                <span className="ol-modal-title">
                  <span className="ol-modal-id">{selectedFields.name}</span>
                  <span className="ol-modal-status ol-modal-status-服务中">产康预约</span>
                </span>
                <button type="button" className="ol-modal-close" onClick={() => setSelectedId('')} title="关闭">×</button>
              </div>
              <div className="ol-modal-body">
                <table className="ol-modal-table">
                  <tbody>
                    <tr>
                      <td className="ol-modal-label">客户姓名</td>
                      <td className="ol-modal-value">{selectedFields.name}</td>
                      <td className="ol-modal-label">房间号</td>
                      <td className="ol-modal-value">{selectedFields.room}</td>
                    </tr>
                    <tr>
                      <td className="ol-modal-label">服务项目</td>
                      <td className="ol-modal-value" colSpan={3}>{selectedFields.service}</td>
                    </tr>
                    <tr>
                      <td className="ol-modal-label">预约日期</td>
                      <td className="ol-modal-value mono">{selectedFields.day}</td>
                      <td className="ol-modal-label">服务时间</td>
                      <td className="ol-modal-value mono">{selectedFields.time}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default NewOrders;
